// AdDataRow.js — Second row showing progress, pacing, and CTR for each ad
// AS-099: metrics loaded async after list renders; skeleton shown while loading

import React from 'react';
import { Badge } from '../../components/ds';
import './AdsList.css';

/**
 * Props:
 *   ad            — row from GET /api/portal/ads (list phase)
 *   summary       — entry from GET /api/portal/ads/metrics, keyed by ad id:
 *                   { executed_impressions: number, clicks: number, click_rate: number }
 *                   May be undefined when metrics haven't loaded yet or the ad
 *                   has no report data.
 *   metricsLoading — true while the metrics fetch is in flight; renders skeleton
 *   metricsHidden  — true when metrics fetch failed; hides metrics entirely to
 *                    avoid showing misleading zeros
 */

function computePacing(ad, summary) {
  const { start_time, end_time } = ad;
  if (!start_time || !end_time) return null;

  const now = new Date();
  const start = new Date(start_time);
  const end = new Date(end_time);

  // Not started yet
  if (now < start) return { state: 'not_started' };

  const totalDays = (end - start) / (1000 * 60 * 60 * 24);
  if (totalDays <= 0) return null;

  // Ended: cap elapsed at total
  const elapsed = now > end ? totalDays : (now - start) / (1000 * 60 * 60 * 24);
  const elapsedRatio = elapsed / totalDays;

  const totalImpressions = summary?.total_impressions ?? ad.total_impressions ?? 0;
  const executedImpressions = summary?.executed_impressions ?? 0;

  // No data
  if (!totalImpressions || totalImpressions === 0) return { state: 'no_data' };
  if (executedImpressions === 0) return { state: 'no_data' };

  const executedRatio = executedImpressions / totalImpressions;

  // 投放初期：走期進度 < 1%，分母趨近 0，pacing 無意義
  if (elapsedRatio < 0.01) return { state: 'early' };

  const pacing = (executedRatio / elapsedRatio) * 100;

  return {
    state: 'ok',
    pacing,
    executedImpressions,
    totalImpressions,
    executedRatio,
    ctr: summary?.clicks != null && executedImpressions > 0
      ? ((summary.clicks / executedImpressions) * 100).toFixed(2)
      : null,
  };
}

function getPacingVariant(pacing) {
  if (pacing >= 90 && pacing <= 110) return 'success';
  if ((pacing >= 80 && pacing < 90) || (pacing > 110 && pacing <= 130)) return 'warning';
  return 'danger';
}

function formatImpressions(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

// AS-099: metricsLoading — show skeleton; metricsHidden — hide metrics entirely on failure
export default function AdDataRow({ ad, summary, metricsLoading, metricsHidden }) {
  // While metrics are loading, show a skeleton placeholder
  if (metricsLoading) {
    return (
      <div className="ad-data-row ad-data-row--loading" aria-busy="true" aria-label="報表載入中">
        <div className="ad-data-row__skeleton-bar ad-data-row__skeleton-bar--progress" />
        <div className="ad-data-row__skeleton-metrics">
          <div className="ad-data-row__skeleton-bar ad-data-row__skeleton-bar--sm" />
          <div className="ad-data-row__skeleton-bar ad-data-row__skeleton-bar--xs" />
          <div className="ad-data-row__skeleton-bar ad-data-row__skeleton-bar--sm" />
          <div className="ad-data-row__skeleton-bar ad-data-row__skeleton-bar--xs" />
        </div>
      </div>
    );
  }

  // Metrics fetch failed — hide metrics section entirely to avoid misleading zeros
  if (metricsHidden) {
    return <div className="ad-data-row ad-data-row--empty" />;
  }

  const result = computePacing(ad, summary);

  if (!result) {
    return (
      <div className="ad-data-row ad-data-row--empty">
        <span className="ad-data-row__no-data">— 無走期資訊</span>
      </div>
    );
  }

  if (result.state === 'not_started') {
    return (
      <div className="ad-data-row ad-data-row--empty">
        <span className="ad-data-row__label">尚未開始</span>
      </div>
    );
  }

  if (result.state === 'no_data') {
    return (
      <div className="ad-data-row ad-data-row--empty">
        <span className="ad-data-row__no-data">⚠ 尚無投放數據</span>
      </div>
    );
  }

  if (result.state === 'early') {
    return (
      <div className="ad-data-row ad-data-row--empty">
        <span className="ad-data-row__label">投放初期</span>
      </div>
    );
  }

  const { pacing, executedImpressions, totalImpressions, executedRatio, ctr } = result;
  const pacingVariant = getPacingVariant(pacing);
  const progressPct = Math.min(executedRatio * 100, 100);
  // Pacing renders as a red pill only when danger; success/warning use plain text
  const pacingIsWarning = pacingVariant === 'danger';

  return (
    <div className="ad-data-row">
      {/* Row 1: progress bar full width */}
      <div className="ad-data-row__progress-bar-wrap">
        <div
          className="ad-data-row__progress-bar-fill"
          style={{ width: `${progressPct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(progressPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Row 2: four metrics separated by middot */}
      <div className="ad-data-row__metrics">
        <span className="ad-data-row__impression-count">
          {formatImpressions(executedImpressions)} / {formatImpressions(totalImpressions)}
        </span>
        <span className="ad-data-row__sep" aria-hidden="true">·</span>
        <span className="ad-data-row__pct">{Math.round(progressPct)}%</span>
        <span className="ad-data-row__sep" aria-hidden="true">·</span>
        {pacingIsWarning ? (
          <Badge variant="danger">Pacing {Math.round(pacing)}%</Badge>
        ) : (
          <span className="ad-data-row__pacing">Pacing {Math.round(pacing)}%</span>
        )}
        <span className="ad-data-row__sep" aria-hidden="true">·</span>
        <span className="ad-data-row__ctr">CTR {ctr !== null ? `${ctr}%` : '—'}</span>
      </div>
    </div>
  );
}
